import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const api = axios.create({
  baseURL: `${API_URL}/api`,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authApi = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  register: (userData) => api.post('/auth/register', userData),
  logout: () => api.post('/auth/logout'),
  me: () => api.get('/auth/me'),
  refresh: () => api.post('/auth/refresh'),
};

// Users API
export const usersApi = {
  getAll: (role) => api.get('/users', { params: { role } }),
  getById: (id) => api.get(`/users/${id}`),
  update: (id, data) => api.put(`/users/${id}`, data),
};

// Categories API
export const categoriesApi = {
  getAll: () => api.get('/categories'),
  create: (data) => api.post('/categories', data),
  update: (id, data) => api.put(`/categories/${id}`, data),
  delete: (id) => api.delete(`/categories/${id}`),
};

// Products API
export const productsApi = {
  getAll: (params) => api.get('/products', { params }),
  getById: (id) => api.get(`/products/${id}`),
  create: (data) => api.post('/products', data),
  update: (id, data) => api.put(`/products/${id}`, data),
  delete: (id) => api.delete(`/products/${id}`),
  getPublic: (params) => api.get('/public/products', { params }),
};

// Sales Orders API
export const salesOrdersApi = {
  getAll: (status) => api.get('/orders/sales', { params: { status } }),
  getById: (id) => api.get(`/orders/sales/${id}`),
  create: (data) => api.post('/orders/sales', data),
  updateStatus: (id, status) => api.put(`/orders/sales/${id}/status?status=${status}`),
};

// Purchase Orders API
export const purchaseOrdersApi = {
  getAll: (status) => api.get('/orders/purchase', { params: { status } }),
  create: (data) => api.post('/orders/purchase', data),
  updateStatus: (id, status) => api.put(`/orders/purchase/${id}/status?status=${status}`),
};

// Delivery Orders API
export const deliveryApi = {
  getAll: (status) => api.get('/delivery', { params: { status } }),
  create: (data) => api.post('/delivery', data),
  ship: (id, trackingNumber) => api.put(`/delivery/${id}/ship?tracking_number=${trackingNumber}`),
  deliver: (id) => api.put(`/delivery/${id}/deliver`),
};

// Invoices API
export const invoicesApi = {
  getAll: (status) => api.get('/invoices', { params: { status } }),
  create: (data) => api.post('/invoices', data),
  recordPayment: (id, data) => api.post(`/invoices/${id}/payment`, data),
};

// Inventory API
export const inventoryApi = {
  getAll: (lowStockOnly) => api.get('/inventory', { params: { low_stock_only: lowStockOnly } }),
  adjust: (data) => api.post('/inventory/adjust', data),
};

// Dashboard API
export const dashboardApi = {
  getStats: () => api.get('/dashboard/stats'),
};

// Public API
export const publicApi = {
  getProducts: (params) => api.get('/public/products', { params }),
  getCategories: () => api.get('/public/categories'),
};

export default api;
